import axios from 'axios';
import { toast } from 'react-toastify';

const axiosInstance = axios.create({
  // baseURL: 'https://api.on-coffee.com/api',
  baseURL: process.env.REACT_APP_PUBLIC_BASE_URL || 'http://localhost:8009/api' || 'https://api.on-coffee.com/api',
  timeout: 50000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
  },
  withCredentials: true,
});

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = await getTokenFromLocalStorage();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export const getTokenFromLocalStorage = () => {
  const tokenLocal = localStorage.getItem('token');
  const token = tokenLocal && JSON.parse(tokenLocal);

  if (token) {
    return token?.token;
  } else {
    return '';
  }
};

export const setTokenTolocalStorage = (token) => {
  try {
    const tokenString = JSON.stringify({ token });
    localStorage.setItem('token', tokenString);
  } catch (error) {
    console.error('Error:', error);
  }
};

export const clearToken = () => {
  try {
    localStorage.removeItem('token');
  } catch (error) {}
};

/**
 * Utility functions để xử lý response và error
 */

// Kiểm tra response có thành công không
export const isSuccessResponse = (response) => {
  return response && (response.access_token || response.data || response.id);
};

/**
 * #190823 — `message` từ backend có thể là MẢNG (class-validator trả nhiều lỗi trường một lúc).
 * Ghép lại thành một câu; mảng thô lọt vào toast sẽ hiện "[object Object]" hoặc chuỗi dính liền.
 */
const normalizeMessage = (message) => {
  if (Array.isArray(message)) {
    const parts = message.map((m) => (typeof m === 'string' ? m : m?.message)).filter(Boolean);
    return parts.length ? parts.join('; ') : '';
  }
  return typeof message === 'string' ? message : '';
};

// Lấy message từ error object
export const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;

  // Lỗi đã qua interceptor bên dưới (dạng chuẩn hoá `{ status, code, message, ... }`) đi đường này.
  const normalized = normalizeMessage(error?.message);
  if (normalized) return normalized;

  // Lỗi axios thô (chưa qua interceptor, vd nơi gọi tự dùng axios) đi 2 đường dưới.
  const fromErrorObj = normalizeMessage(error?.response?.data?.error?.message);
  if (fromErrorObj) return fromErrorObj;
  const fromData = normalizeMessage(error?.response?.data?.message);
  if (fromData) return fromData;

  // #190823 — hết đường lấy câu chữ thì ÍT NHẤT cũng phải nói mã lỗi/HTTP status, đừng để người
  // dùng nhận đúng một câu chung chung không tra được gì (đây chính là ca sếp báo ở #184651).
  const status = error?.status ?? error?.response?.status;
  const code = error?.code ?? error?.response?.data?.error?.code;
  const hint = [code, status && `HTTP ${status}`].filter(Boolean).join(' · ');
  return hint ? `Có lỗi xảy ra (${hint})` : 'Có lỗi xảy ra';
};

// Lấy error code từ error object
export const getErrorCode = (error) => {
  if (error?.code) return error.code;
  if (error?.response?.data?.error?.code) return error.response.data.error.code;
  return null;
};

// Lấy trace ID từ error object
export const getTraceId = (error) => {
  if (error?.traceId) return error.traceId;
  if (error?.response?.data?.error?.traceId) return error.response.data.error.traceId;
  return null;
};

axiosInstance.interceptors.response.use(
  (res) => {
    // Xử lý response thành công (HTTP 2xx)
    // Trả về data trực tiếp từ response
    return res.data;
  },
  (error) => {
    const { response } = error || {};
    let errMsg = '';

    try {
      // Xử lý lỗi từ API theo format mới
      if (response?.data?.error) {
        // Format lỗi mới từ api-client.md:
        // {
        //   "error": {
        //     "code": "CONFLICT",
        //     "message": "Bạn đã có ca trùng thời gian",
        //     "details": null,
        //     "traceId": "req-abc123"
        //   }
        // }
        const errorData = response.data.error;
        // #190823 — giữ nguyên câu backend gửi; chỉ ghép nếu là mảng (class-validator).
        errMsg = normalizeMessage(errorData.message) || 'Có lỗi xảy ra';

        // Log chi tiết lỗi để debug
        console.error('API Error:', {
          code: errorData.code,
          message: errorData.message,
          details: errorData.details,
          traceId: errorData.traceId,
          status: response.status,
        });
      } else if (response?.data?.message) {
        // Fallback cho format cũ
        errMsg = normalizeMessage(response.data.message) || 'Có lỗi xảy ra';
      } else if (response?.statusText) {
        // Fallback cho HTTP status text
        errMsg = response.statusText;
      } else {
        errMsg = error.message || 'Có lỗi xảy ra';
      }
    } catch (parseError) {
      console.error('Error parsing response:', parseError);
      errMsg = 'Có lỗi xảy ra khi xử lý phản hồi';
    }

    // Hiển thị thông báo lỗi
    // toast(errMsg, { type: 'error' });

    // Trả về error object chuẩn hóa
    const normalizedError = {
      status: response?.status,
      code: response?.data?.error?.code,
      message: errMsg,
      details: response?.data?.error?.details,
      traceId: response?.data?.error?.traceId,
      originalError: error,
    };

    return Promise.reject(normalizedError);
  },
);

class apiClient {
  get(config) {
    return this.request({ ...config, method: 'GET', withCredentials: false });
  }

  post(config) {
    return this.request({ ...config, method: 'POST', withCredentials: false });
  }

  put(config) {
    return this.request({ ...config, method: 'PUT', withCredentials: false });
  }

  patch(config) {
    return this.request({ ...config, method: 'PATCH', withCredentials: false });
  }

  delete(config) {
    return this.request({
      ...config,
      method: 'DELETE',
      withCredentials: false,
    });
  }

  request(config) {
    return axiosInstance.request(config);
  }
}

const apiClientInstance = new apiClient();

export default apiClientInstance;
